// src/pages/TgenTokenPage.js
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { fetchTgenPoolData, TGEN_POOL_PAGE_URL } from "../utils/tgenPoolApi";

/* =========================
   CONSTANTS
========================= */

const BUY_URL =
  "https://dex.coinmarketcap.com/token/bsc/0xdd3f2d1b0ec5ea81cdf70e79c713d35ef8d530e1/";

/* =========================
   STYLES
========================= */

const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 4.5rem 0 0 0;
  position: relative;
  overflow: hidden;
  background:
    linear-gradient(135deg, #f0f9ff 0%, #ffffff 55%, #fef6e4 100%);
`;

const Inner = styled.div`
  padding: 0 10%;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

/* HERO */

const Hero = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 3.5rem;
  padding-top: 1.5rem;
  padding-bottom: 3.5rem;

  @media (max-width: 960px) {
    flex-direction: column;
    text-align: center;
  }
`;

const HeroLeft = styled.div`
  flex: 1;
  max-width: 560px;
`;

const Kicker = styled.p`
  font-size: 0.75rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 0.7rem;
  display: inline-block;
  padding: 0.4rem 1rem;
  background: var(--primary-light);
  border-radius: 999px;
  border: 1px solid var(--border-color);
`;

const HeroTitle = styled.h1`
  font-size: 2.6rem;
  line-height: 1.15;
  margin: 0 0 1rem;
  font-weight: 800;
  color: var(--text-color);

  @media (max-width: 720px) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 0.98rem;
  color: var(--text-secondary-color);
  line-height: 1.7;
  margin-bottom: 1.6rem;
`;

const HeroBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem 1rem;
  margin-bottom: 1.8rem;

  span {
    font-size: 0.82rem;
    color: var(--text-secondary-color);
    padding: 0.35rem 0.8rem;
    border-radius: 999px;
    border: 1px solid var(--border-color);
    background: var(--bg-secondary);
  }

  @media (max-width: 960px) {
    justify-content: center;
  }
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;

  @media (max-width: 960px) {
    justify-content: center;
  }
`;

const PrimaryCta = styled.a`
  padding: 0.85rem 1.9rem;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  border: none;
  box-shadow:
    0 14px 40px rgba(41, 171, 226, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  cursor: pointer;
`;

const GhostCta = styled.a`
  padding: 0.85rem 1.9rem;
  border-radius: 999px;
  background: transparent;
  color: var(--text-secondary-color);
  font-size: 0.95rem;
  font-weight: 500;
  text-decoration: none;
  border: 1px solid var(--border-color);
  cursor: pointer;
`;

/* HERO RIGHT â€“ LIVE STATS CARD */

const HeroRight = styled.div`
  flex: 1;
  max-width: 520px;
  width: 100%;
`;

const MarketCard = styled.div`
  background:
    linear-gradient(135deg, var(--bg-surface), var(--bg-surface)),
    var(--bg-surface);
  border-radius: 20px;
  border: 1px solid var(--border-color);
  box-shadow:
    var(--shadow-md);
  padding: 1.2rem 1.3rem 1.4rem;
`;

const MarketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.8rem;
`;

const MarketTitle = styled.h2`
  font-size: 0.98rem;
  font-weight: 600;
  color: var(--text-color);
`;

const MarketSub = styled.span`
  font-size: 0.8rem;
  color: var(--text-muted);
`;

const MarketBody = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem 1.5rem;
  margin-top: 0.6rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatBlock = styled.div`
  font-size: 0.86rem;
  color: var(--text-secondary-color);
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
  margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--text-color);
`;

const StatChange = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: ${({ positive, negative }) =>
    positive ? "#22c55e" : negative ? "#ef4444" : "var(--text-color)"};
`;

const PairRow = styled.div`
  margin-top: 1rem;
  padding-top: 0.9rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const PairText = styled.div`
  font-size: 0.8rem;
  color: var(--text-muted);
`;

const DexLinkButton = styled.a`
  padding: 0.6rem 1.2rem;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  border: 1px solid var(--border-color);
  color: var(--text-color);
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
`;

/* GENERIC SECTIONS */

const Section = styled.section`
  padding: 3rem 0;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 0.7rem;
  color: #0c1f2e !important;
`;

const SectionIntro = styled.p`
  font-size: 0.96rem;
  color: var(--text-secondary-color);
  max-width: 640px;
  margin: 0 auto 2rem;
  line-height: 1.7;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0;
`;

const Card = styled.div`
  background:
    linear-gradient(135deg, rgba(139, 92, 255, 0.05), var(--bg-secondary)),
    var(--bg-surface);
  border: 1px solid var(--border-color);
  border-right: 0;
  border-bottom: 0;
  padding: 2.5rem 2.5rem;
  text-align: left;

  @media (max-width: 720px) {
    padding: 2rem 1.7rem;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const CardText = styled.p`
  font-size: 0.94rem;
  color: var(--text-secondary-color);
  line-height: 1.7;
  margin-bottom: 0.5rem;
`;

/* BOTTOM CTA */

const BottomCta = styled.section`
  padding: 3.5rem 0 4rem;
  text-align: center;
`;

const BottomCtaText = styled.p`
  font-size: 0.96rem;
  color: var(--text-secondary-color);
  margin-bottom: 1.3rem;
`;

/* =========================
   HELPERS
========================= */

const formatUsd = (value) => {
  if (value == null) return "--";
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(2)}k`;
  return `$${value.toFixed(4)}`;
};

const formatSupply = (value) => {
  if (value == null) return "â€”";
  const v = Number(value);
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(2)}B`;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(2)}k`;
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

/* =========================
   PAGE COMPONENT
========================= */

const TokenPage = () => {
  const [price, setPrice] = useState(null);
  const [change24h, setChange24h] = useState(null);
  const [totalSupply, setTotalSupply] = useState(null);
  const [circulatingSupply, setCirculatingSupply] = useState(null);
  const [fdv, setFdv] = useState(null);
  const [marketCap, setMarketCap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let intervalId;

    const fetchData = async () => {
      try {
        const data = await fetchTgenPoolData();
        const priceUsd = data.priceUsd;
        const change = data.change24h;
        const fdvVal = data.fdv;
        const mcapVal = data.marketCap;

        const total = fdvVal != null && priceUsd != null ? fdvVal / priceUsd : null;
        const circ = mcapVal != null && priceUsd != null ? mcapVal / priceUsd : null;

        setPrice(priceUsd);
        setChange24h(change != null ? Number(change) : null);
        setFdv(fdvVal);
        setMarketCap(mcapVal);
        setTotalSupply(total);
        setCirculatingSupply(circ);
        setLoading(false);
      } catch (err) {
        console.error("GeckoTerminal fetch error:", err);
        setLoading(false);
      }
    };

    fetchData();
    intervalId = setInterval(fetchData, 30000);

    return () => clearInterval(intervalId);
  }, []);

  const formattedPrice =
    price != null ? `$${price.toFixed(price < 0.01 ? 6 : 4)}` : "--";

  const formattedChange =
    change24h != null
      ? `${change24h >= 0 ? "+" : ""}${change24h.toFixed(2)}%`
      : "--";

  const tokenSchema = {
    "@context": "https://schema.org",
    "@type": "Cryptocurrency",
    name: "Trend Genie",
    alternateName: "TGEN",
    symbol: "TGEN",
    description:
      "Trend Genie (TGEN) is a BEP-20 utility token on BNB Smart Chain powering AI trading tools, the RugShield DeFi scanner, and on-chain gaming like Trend Genie Ludo Arena.",
    url: "https://www.trendgenie.io/tgen-token",
    mainEntityOfPage: "https://www.trendgenie.io/tgen-token",
    network: "BNB Smart Chain",
    contractAddress: "0xdD3f2d1B0EC5ea81CDf70e79C713D35EF8d530E1",
  };

  return (
    <PageWrapper>
      <Helmet>
        <title>
          Trend Genie (TGEN) Token | Utility, Live Price & Gaming Ecosystem
        </title>
        <meta
          name="description"
          content="Discover Trend Genie (TGEN), a BEP-20 utility token on BNB Smart Chain that powers the Trend Genie AI trading app, RugShield DeFi safety scanner, and Web3 gaming like Ludo Arena."
        />
        <meta
          name="keywords"
          content="Trend Genie, TGEN token, BNB Smart Chain, BEP-20, crypto utility token, DeFi safety, RugShield, Ludo Arena, AI trading signals"
        />
        <link rel="canonical" href="https://www.trendgenie.io/tgen-token" />
        <script type="application/ld+json">
          {JSON.stringify(tokenSchema)}
        </script>
      </Helmet>

      <Inner>
        {/* HERO WITH LIVE MARKET CARD */}
        <Hero>
          <HeroLeft>
            <Kicker>$TGEN Utility Token Â· Trend Genie</Kicker>
            <HeroTitle>Trend Genie (TGEN) Token</HeroTitle>
            <HeroSubtitle>
              TGEN is the native utility token of the Trend Genie ecosystem â€“
              connecting the AI trading app, RugShield DeFi scanner, and
              on-chain games like Trend Genie Ludo Arena into one unified,
              utility-driven experience instead of pure speculation.
            </HeroSubtitle>

            <HeroBadges>
              <span>Utility across tools & games</span>
              <span>Built on BNB Smart Chain</span>
              <span>Designed for real traders</span>
            </HeroBadges>

            <CtaRow>
              <PrimaryCta href={BUY_URL} target="_blank" rel="noreferrer">
                Buy TGEN on CMC DEX
              </PrimaryCta>
              <GhostCta
                href="https://app.trendgenie.io"
                target="_blank"
                rel="noreferrer"
              >
                Open Trend Genie Web App
              </GhostCta>
            </CtaRow>
          </HeroLeft>

          <HeroRight>
            <MarketCard>
              <MarketHeader>
                <div>
                  <MarketTitle>$TGEN Live Price & Stats</MarketTitle>
                  <MarketSub>BSC · GeckoTerminal Pool · TGEN / USDT</MarketSub>
                </div>
              </MarketHeader>

              <MarketBody>
                <StatBlock>
                  <StatLabel>Price</StatLabel>
                  <StatValue>{loading ? "Loadingâ€¦" : formattedPrice}</StatValue>
                </StatBlock>

                <StatBlock>
                  <StatLabel>24h Change</StatLabel>
                  <StatChange
                    positive={change24h > 0}
                    negative={change24h < 0}
                  >
                    {loading ? "â€”" : formattedChange}
                  </StatChange>
                </StatBlock>

                <StatBlock>
                  <StatLabel>Total Supply</StatLabel>
                  <StatValue>
                    {loading ? "â€”" : `${formatSupply(totalSupply)} TGEN`}
                  </StatValue>
                </StatBlock>

                <StatBlock>
                  <StatLabel>Circulating Supply</StatLabel>
                  <StatValue>
                    {loading ? "â€”" : `${formatSupply(circulatingSupply)} TGEN`}
                  </StatValue>
                </StatBlock>

                <StatBlock>
                  <StatLabel>FDV</StatLabel>
                  <StatValue>{loading ? "â€”" : formatUsd(fdv)}</StatValue>
                </StatBlock>
              </MarketBody>

              <PairRow>
                <PairText>
                  Live on-chain data via GeckoTerminal. Values update every 30 seconds.
                </PairText>
                <DexLinkButton
                  href={TGEN_POOL_PAGE_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  View pool on GeckoTerminal ->
                </DexLinkButton>
              </PairRow>
            </MarketCard>
          </HeroRight>
        </Hero>

        {/* WHAT IS TGEN */}
        <Section>
          <SectionTitle>What Is Trend Genie (TGEN)?</SectionTitle>
          <SectionIntro>
            Trend Genie (TGEN) is a BEP-20 token on BNB Smart Chain that powers
            real products â€“ not just a chart. TGEN sits at the centre of Trend
            Genie&apos;s AI trading app, RugShield DeFi safety scanner, and
            upcoming Web3 gaming experiences, giving holders practical ways to
            use the token inside the ecosystem.
          </SectionIntro>

          <CardGrid>
            <Card>
              <CardTitle>Utility for Traders & Players</CardTitle>
              <CardText>
                TGEN is integrated into Trend Genie tools so traders and gamers
                can unlock more utility over time. From usage-based perks in
                the AI trading app to in-game value inside Ludo Arena, the
                token is designed to be spent, used and circulated â€“ not just
                held and forgotten.
              </CardText>
            </Card>
            <Card>
              <CardTitle>Backed by Real Products</CardTitle>
              <CardText>
                Instead of shipping a token first and searching for use cases
                later, Trend Genie started by building live products: an Android
                AI trading app and RugShield DeFi scanner. TGEN now layers
                utility on top of these tools, so value flows from real
                activity, not just hype.
              </CardText>
            </Card>
            <Card>
              <CardTitle>Transparent & On-Chain</CardTitle>
              <CardText>
                TGEN runs on BNB Smart Chain as a BEP-20 token with fully
                visible on-chain activity. Liquidity, transactions and game
                prize pools can all be tracked in real time, giving the
                community a clear view into how the token is being used.
              </CardText>
            </Card>
          </CardGrid>
        </Section>

        {/* CORE UTILITY */}
        <Section>
          <SectionTitle>Core TGEN Utilities</SectionTitle>
          <SectionIntro>
            As the ecosystem grows, TGEN will be used across trading, gaming and
            DeFi safety â€“ making it the common layer that connects different
            Trend Genie experiences.
          </SectionIntro>

          <CardGrid>
            <Card>
              <CardTitle>Game Entry Fees & Prize Pools</CardTitle>
              <CardText>
                In Trend Genie Ludo Arena, players use TGEN as the entry fee to
                join real-time multiplayer matches. Entry fees form an on-chain
                prize pool â€“ the winning player receives the majority of the
                rewards, while a small percentage goes back to the project
                treasury to fund development and future utilities.
              </CardText>
            </Card>
            <Card>
              <CardTitle>Tournaments & Seasonal Events</CardTitle>
              <CardText>
                Future updates will introduce ranked ladders, seasonal
                tournaments and special event rooms that all run on TGEN.
                Sponsored events and collaborations can plug into the same
                prize-pool model, driving new demand for players who want to
                compete at higher stakes.
              </CardText>
            </Card>
            <Card>
              <CardTitle>Premium Access & Memberships</CardTitle>
              <CardText>
                Planned utilities include premium memberships and season passes
                paid in TGEN. These can unlock discounted game fees, exclusive
                rooms, boosted rewards, or cosmetic upgrades â€“ rewarding players
                and traders who stay active in the ecosystem.
              </CardText>
            </Card>
          </CardGrid>
        </Section>

        {/* FUTURE PLANS */}
        <Section>
          <SectionTitle>Future Plans for the TGEN Ecosystem</SectionTitle>
          <SectionIntro>
            TGEN is the starting point for a broader Trend Genie ecosystem where
            charts, contracts and games all sit under one roof. The roadmap
            extends beyond a single app or token pair.
          </SectionIntro>

          <CardGrid>
            <Card>
              <CardTitle>More Games & Mini-Game Hub</CardTitle>
              <CardText>
                Ludo Arena is the first Trend Genie game, but not the last. Over
                time, the same infrastructure can support multiple TGEN-powered
                mini-games, hosted arenas and co-branded rooms where different
                communities compete and earn on-chain.
              </CardText>
            </Card>
            <Card>
              <CardTitle>NFTs, Cosmetics & Upgrades</CardTitle>
              <CardText>
                Future releases may introduce NFT-based boards, dice skins and
                character cosmetics tied to TGEN. These items can be bought,
                earned or traded, adding a collection and identity layer on top
                of the core play-to-win experience.
              </CardText>
            </Card>
            <Card>
              <CardTitle>Integrations & Partnerships</CardTitle>
              <CardText>
                As Trend Genie grows, the goal is to integrate with other DeFi
                tools, wallets and gaming communities. Partner projects could
                host branded Ludo rooms, tournaments or campaigns â€“ all settled
                in TGEN, bringing fresh users and activity into the ecosystem.
              </CardText>
            </Card>
          </CardGrid>
        </Section>

        {/* BOTTOM CTA */}
        <BottomCta>
          <SectionTitle>Get Started With TGEN</SectionTitle>
          <BottomCtaText>
            Explore the live TGEN stats, join upcoming games and track how the
            token powers real tools and utilities across the Trend Genie stack.
          </BottomCtaText>
          <CtaRow style={{ justifyContent: "center" }}>
            <PrimaryCta href={BUY_URL} target="_blank" rel="noreferrer">
              Buy TGEN on CMC DEX
            </PrimaryCta>
            <GhostCta
              href="https://app.trendgenie.io"
              target="_blank"
              rel="noreferrer"
            >
              Open Trend Genie Web App
            </GhostCta>
          </CtaRow>
        </BottomCta>
      </Inner>
    </PageWrapper>
  );
};

export default TokenPage;



